import React, { useState } from "react";
import { Alert, Select, Stack } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../../context/AppContext";
import { Employee } from "../../../types/salon";
import {
  CommissionRecord,
  CreateCommissionInput,
} from "../../../types/commission";
import { getActiveCommission } from "../../../utils/commissionUtils";
import NewCommissionForm, {
  CommissionFormController,
} from "../NewCommissionForm";
import { staffName } from "./commissionDisplay";
import {
  CommissionFormActions,
  CommissionFormFrame,
} from "./commissionFormFrame";
import CommissionDrawerView from "./CommissionDrawerView";

export interface CommissionFormViewProps {
  employees: Employee[];
  commissionsByStaff: Record<string, CommissionRecord[]>;
  /** The record being edited; null to add a new rate. */
  editing: CommissionRecord | null;
  /** Whose rate; null asks for a team member first (page-level "Add Rate"). */
  staffId: string | null;
  /** Back to the rate details; omitted when opened from the page. */
  onBack?: () => void;
  onCancel: () => void;
  /** Saved for this staff member. */
  onSaved: (staffId: string) => void;
  /** The form gained or lost unsaved changes. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Lets the drawer save the form from its unsaved-changes dialog. */
  controllerRef?: React.Ref<CommissionFormController>;
}

/**
 * The drawer's add / edit screen: the commission form (and its "Rates by
 * service" screen) in the drawer layout. A new rate starts from the staff
 * member's current one, dated today.
 */
const CommissionFormView: React.FC<CommissionFormViewProps> = ({
  employees,
  commissionsByStaff,
  editing,
  staffId: initialStaffId,
  onBack,
  onCancel,
  onSaved,
  onDirtyChange,
  controllerRef,
}) => {
  const { createCommissions } = useApp();
  const [staffId, setStaffId] = useState<string | null>(
    editing?.staff_id ?? initialStaffId,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = editing ? "Edit commission rate" : "New commission rate";
  const submitLabel = editing ? "Save Changes" : "Save Rate";

  const handleSubmit = async (input: CreateCommissionInput): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createCommissions({ commissions: [input] });
    } catch (err: any) {
      console.error("Error saving commission:", err);
      setError(err?.message || "Failed to save commission. Please try again.");
      // Tell the form (and an unsaved-changes "Save") the save didn't happen.
      throw err;
    } finally {
      setIsSubmitting(false);
    }
    onSaved(input.staff_id);
  };

  const errorAlert = error && (
    <Alert
      icon={<IconAlertCircle size={16} />}
      title="Error"
      color="red"
      withCloseButton
      onClose={() => setError(null)}
    >
      {error}
    </Alert>
  );
  // Only when adding from the page: pick whose rate it is first.
  const picker = !initialStaffId && !editing && (
    <Select
      label="Team member"
      withAsterisk
      placeholder="Search staff..."
      searchable
      data={employees.map((e) => ({ value: e.id, label: staffName(e) }))}
      value={staffId}
      onChange={setStaffId}
    />
  );

  const frame: CommissionFormFrame = (screen) => (
    <CommissionDrawerView
      title={screen.title ?? title}
      onBack={screen.onBack ?? onBack}
      toolbar={screen.toolbar}
      // Rates by service: the list runs straight on from its search box.
      headerDivider={screen.screen === "form"}
      footer={screen.footer}
    >
      {screen.screen === "form" ? (
        <Stack gap="md">
          {errorAlert}
          {picker}
          {screen.children}
        </Stack>
      ) : (
        screen.children
      )}
    </CommissionDrawerView>
  );

  if (!staffId) {
    return frame({
      screen: "form",
      children: null,
      footer: (
        <CommissionFormActions
          cancelLabel="Cancel"
          onCancel={onCancel}
          submitLabel={submitLabel}
          submitDisabled
        />
      ),
    });
  }

  return (
    <NewCommissionForm
      // A different team member starts a fresh form from their rate.
      key={staffId}
      staffId={staffId}
      existingCommission={editing}
      prefillFrom={
        editing ? null : getActiveCommission(commissionsByStaff[staffId] ?? [])
      }
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isLoading={isSubmitting}
      cancelLabel="Cancel"
      submitLabel={submitLabel}
      frame={frame}
      onDirtyChange={onDirtyChange}
      controllerRef={controllerRef}
    />
  );
};

export default CommissionFormView;
